

import Vue from 'vue'
import Component from "vue-class-component";
import {Prop, Watch} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {IPromotion, PROMOTION_TYPES, PROMOTION_VALUE_TYPES} from "@/model/Service/promotionInterfaces";
import {IServiceProvider} from "@/model/Service/serviceProviderInterfaces";
import {IOnOffHandlers} from "bonomo/dist";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import CreateButton from "@/components/shared/CreateButton.vue";
import ServiceProviderSearch from "@/components/serviceProvider/widget/ServiceProviderSearch.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import SPNav from "@/components/serviceProvider/widget/SPNav.vue";

const serviceProviderPromotionModule = namespace("serviceProviderPromotionModule");
const serviceProviderModule = namespace("serviceProviderModule");

interface PromotionForm {
  promo_code: string;
  type: string;
  value: string;
  value_type: string;
  expiration_date: string;
  min_total: string;
  notes: string;
  active: boolean;
  display_app: boolean;
}

@Component({
  components: {
    SPNav,
    CreateButton,
    ServiceProviderSearch,
    PaginationBar,
  }
})
export default class ServiceProvidersPromotions extends Vue {

  @Prop()
  protected spId!: number;

  @Watch('spId')
  onSpIdChanged() {
    this.loading = true;
    this.search = '';
    this.fetchServiceProvider(this.spId);
    this.fetchPromotions({ spId: this.spId });
  }

  @serviceProviderPromotionModule.Action('fetchPromotions')
  protected fetchPromotions!: (data: { spId: number }) => any;
  @serviceProviderPromotionModule.Action('createPromotion')
  protected createPromotion!: (data: { spId: number, promotion: Partial<IPromotion> }) => any;
  @serviceProviderPromotionModule.Action('updatePromotion')
  protected updatePromotion!: (data: { spId: number, promotionId: number, promotion: Partial<IPromotion> }) => any;
  @serviceProviderPromotionModule.Action('deletePromotion')
  protected deletePromotionAction!: (data: { spId: number, promotionId: number }) => any;
  @serviceProviderPromotionModule.Getter('promotions')
  protected promotions!: IPromotion[];

  @serviceProviderModule.Action('fetchServiceProvider')
  protected fetchServiceProvider!: (spId: number) => any;
  @serviceProviderModule.Getter('serviceProvider')
  protected serviceProvider!: IServiceProvider;

  protected loading: boolean = true;
  protected search: string = '';
  protected currentPage: number = 1;
  protected selectedPerPage: number = 15;
  protected readonly perPageOptions: number[] = [15, 30, 50, 100];
  protected showFormDialog: boolean = false;
  protected showDeleteDialog: boolean = false;
  protected formValid: boolean = false;
  protected formSaving: boolean = false;
  protected deleting: boolean = false;
  protected editingPromotion: IPromotion | null = null;
  protected deletingPromotion: IPromotion | null = null;
  protected ons: IOnOffHandlers = {};

  protected form: PromotionForm = this.emptyForm();

  protected get headers(): BetterDataTableHeader[] {
    return [
      { text: 'ID', value: 'id', sortable: true },
      { text: this.$t('services.provider.promotions.fields.promo_code'), value: 'promo_code', sortable: true },
      { text: this.$t('common.type'), value: 'type', sortable: true },
      { text: this.$t('services.provider.promotions.fields.value'), value: 'value', sortable: true },
      { text: this.$t('services.provider.promotions.fields.expiration_date'), value: 'expiration_date', sortable: true },
      { text: this.$t('common.active'), value: 'active', sortable: true, align: 'center' },
      { text: this.$t('services.provider.promotions.fields.display_app'), value: 'display_app', sortable: false, align: 'center' },
      { text: '', value: 'actions', sortable: false, align: 'end' },
    ];
  }

  protected get filteredPromotions(): IPromotion[] {
    return this.promotions || [];
  }

  protected get totalItems(): number {
    return this.filteredPromotions.length;
  }

  protected get lastPage(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.selectedPerPage));
  }

  protected onPageChange(page: number) {
    this.currentPage = page;
  }

  protected onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.currentPage = 1;
  }

  protected get typeOptions() {
    return Object.entries(PROMOTION_TYPES).map(([k, v]) => ({ text: v, value: k }));
  }

  protected get valueTypeOptions() {
    return Object.entries(PROMOTION_VALUE_TYPES).map(([k, v]) => ({ text: v, value: k }));
  }

  created() {
    this.ons = this.$ons({
      'backend.call.OK.promotionsLoaded': this.onPromotionsLoaded,
      'backend.call.OK.promotionCreated': this.onPromotionSaved,
      'backend.call.OK.promotionUpdated': this.onPromotionSaved,
      'backend.call.OK.promotionDeleted': this.onPromotionDeleted,
      'backend.call.KO.promotionCreated': this.onFormError,
      'backend.call.KO.promotionUpdated': this.onFormError,
      'backend.call.KO.promotionDeleted': this.onFormError,
    });

    this.fetchServiceProvider(this.spId);
    this.fetchPromotions({ spId: this.spId });
  }

  beforeDestroy() {
    this.$offs(this.ons);
  }

  protected onPromotionsLoaded() {
    this.loading = false;
  }

  protected emptyForm(): PromotionForm {
    return {
      promo_code: '',
      type: 'one_time',
      value: '0',
      value_type: 'percentage',
      expiration_date: '',
      min_total: '0',
      notes: '',
      active: true,
      display_app: false,
    };
  }

  protected openCreateDialog() {
    this.editingPromotion = null;
    this.form = this.emptyForm();
    this.showFormDialog = true;
  }

  protected openEditDialog(promotion: IPromotion) {
    this.editingPromotion = promotion;
    this.form = {
      promo_code: promotion.promo_code,
      type: promotion.type,
      value: String(promotion.value),
      value_type: promotion.value_type,
      expiration_date: promotion.expiration_date ? promotion.expiration_date.substring(0, 10) : '',
      min_total: String(promotion.min_total || 0),
      notes: promotion.notes || '',
      active: !!promotion.active,
      display_app: !!promotion.display_app,
    };
    this.showFormDialog = true;
  }

  protected closeFormDialog() {
    this.showFormDialog = false;
    this.editingPromotion = null;
    this.formSaving = false;
  }

  protected onSavePromotion() {
    this.formSaving = true;
    if (this.editingPromotion) {
      this.updatePromotion({
        spId: this.spId,
        promotionId: this.editingPromotion.id,
        promotion: this.form as any,
      });
    } else {
      this.createPromotion({
        spId: this.spId,
        promotion: this.form as any,
      });
    }
  }

  protected onPromotionSaved() {
    this.formSaving = false;
    this.closeFormDialog();
    this.fetchPromotions({ spId: this.spId });
  }

  protected onFormError() {
    this.formSaving = false;
    this.deleting = false;
  }

  protected confirmDelete(promotion: IPromotion) {
    this.deletingPromotion = promotion;
    this.showDeleteDialog = true;
  }

  protected onDeletePromotion() {
    if (!this.deletingPromotion) return;
    this.deleting = true;
    this.deletePromotionAction({
      spId: this.spId,
      promotionId: this.deletingPromotion.id,
    });
  }

  protected onPromotionDeleted() {
    this.deleting = false;
    this.showDeleteDialog = false;
    this.deletingPromotion = null;
    this.fetchPromotions({ spId: this.spId });
  }

  protected onServiceProviderSelected(spId: number) {
    if (this.$route.params.spId != `${spId}`) {
      this.$router.push({ name: 'serviceProvidersPromotions', params: { spId: `${spId}` } });
    }
  }

}

